import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import './Admin.css';

const Inventory = () => {
  const { token } = useAuth();
  const { language, isRTL } = useLanguage();
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState({ totalProducts: 0, inStock: 0, lowStock: 0, outOfStock: 0, totalStockValue: 0 });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(() => {
    try { return localStorage.getItem('inv_filter') || 'all'; } catch { return 'all'; }
  });
  const [search, setSearch] = useState('');
  const [adjusting, setAdjusting] = useState(null);
  const [adjustValue, setAdjustValue] = useState('');
  const [showLogs, setShowLogs] = useState(false);
  const [logProductId, setLogProductId] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { try { localStorage.setItem('inv_filter', filter); } catch {} }, [filter]);

  const fetchAll = async () => {
    try {
      const [prodRes, sumRes, logRes] = await Promise.all([
        fetch('/api/products?includeHidden=true', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/products/inventory/summary', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/products/inventory/logs?limit=100', { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (prodRes.ok) setProducts(await prodRes.json());
      if (sumRes.ok) setSummary(await sumRes.json());
      if (logRes.ok) setLogs(await logRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const adjustStock = async (productId, adjustment, reason) => {
    try {
      const res = await fetch(`/api/products/${productId}/stock`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ adjustment, reason })
      });
      if (res.ok) {
        fetchAll();
        setAdjusting(null);
        setAdjustValue('');
      }
    } catch (e) { console.error(e); }
  };

  const setStock = async (productId, stock) => {
    try {
      const res = await fetch(`/api/products/${productId}/stock`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stock, reason: 'manual_set' })
      });
      if (res.ok) {
        fetchAll();
        setAdjusting(null);
        setAdjustValue('');
      }
    } catch (e) { console.error(e); }
  };

  const recalculateStock = async () => {
    const msg = language === 'ar'
      ? 'سيتم إعادة حساب المخزون بناءً على الطلبات. هل تريد المتابعة؟'
      : 'This will recalculate stock based on orders. Continue?';
    if (!window.confirm(msg)) return;

    setRecalculating(true);
    try {
      const res = await fetch('/api/products/recalculate-stock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({})
      });
      if (res.ok) {
        const result = await res.json();
        alert(language === 'ar'
          ? `تم إعادة حساب المخزون لـ ${result.products.length} منتج`
          : `Stock recalculated for ${result.products.length} products`);
        fetchAll();
      }
    } catch (e) {
      alert(language === 'ar' ? 'فشل في إعادة حساب المخزون' : 'Failed to recalculate stock');
    } finally {
      setRecalculating(false);
    }
  };

  const quickAdjust = (productId, amount) => {
    adjustStock(productId, amount, 'restock');
  };

  const filteredProducts = products.filter(p => {
    if (filter === 'low') return p.stock > 0 && p.stock <= 5;
    if (filter === 'out') return p.stock === 0;
    return true;
  }).filter(p => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (p.name && p.name.toLowerCase().includes(s)) || (p.nameAr && p.nameAr.includes(search));
  });

  const getStockBadge = (stock) => {
    if (stock === 0) return { bg: '#fef2f2', color: '#dc2626', border: '#fecaca', text: language === 'ar' ? 'نفذ' : 'Out' };
    if (stock <= 5) return { bg: '#fffbeb', color: '#d97706', border: '#fde68a', text: language === 'ar' ? 'منخفض' : 'Low' };
    return { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', text: language === 'ar' ? 'متوفر' : 'OK' };
  };

  const getReasonLabel = (reason) => {
    const labels = {
      order_created: { en: 'Order Created', ar: 'طلب جديد' },
      order_cancelled: { en: 'Order Cancelled', ar: 'طلب ملغي' },
      manual_adjustment: { en: 'Manual Adjust', ar: 'تعديل يدوي' },
      manual_set: { en: 'Stock Set', ar: 'تعيين المخزون' },
      restock: { en: 'Restock', ar: 'إعادة تعبئة' }
    };
    const l = labels[reason] || { en: reason, ar: reason };
    return language === 'ar' ? l.ar : l.en;
  };

  const getReasonColor = (reason) => {
    if (reason === 'order_created') return '#3b82f6';
    if (reason === 'order_cancelled') return '#f59e0b';
    if (reason === 'restock') return '#10b981';
    return '#6b7280';
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const displayLogs = logProductId
    ? logs.filter(l => l.productId === logProductId)
    : logs;

  // Compute total stock across all products
  const totalUnits = products.reduce((sum, p) => sum + (p.stock || 0), 0);
  const totalSold = products.reduce((sum, p) => sum + Math.max(0, (p.baseStock || 0) - (p.stock || 0)), 0);

  if (loading) return (
    <div className="admin-page"><div className="container"><div className="loading-box"><div className="spinner"></div></div></div></div>
  );

  return (
    <div className={`admin-page ${isRTL ? 'rtl' : ''}`}>
      <div className="container">
        {/* Nav */}
        <div className="admin-card" style={{ padding: '12px 16px', marginBottom: '20px', ...(window.innerWidth >= 768 ? { position: 'sticky', top: '72px', zIndex: 99 } : {}) }}>
          <div className="admin-nav" style={{ marginBottom: 0 }}>
            <Link to="/dashboard4">{language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}</Link>
            <Link to="/dashboard4/products">{language === 'ar' ? 'المنتجات' : 'Products'}</Link>
            <Link to="/dashboard4/orders">{language === 'ar' ? 'الطلبات' : 'Orders'}</Link>
            <Link to="/dashboard4/create-order">{language === 'ar' ? 'إنشاء طلب' : 'Create Order'}</Link>
            <Link to="/dashboard4/inventory" className="active">{language === 'ar' ? 'المخزون' : 'Inventory'}</Link>
            <Link to="/dashboard4/stats">{language === 'ar' ? 'الإحصائيات' : 'Stats'}</Link>
            <Link to="/dashboard4/settings">{language === 'ar' ? 'الإعدادات' : 'Settings'}</Link>
          </div>
        </div>

        {/* ====== Hero Summary — Stats style ====== */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr',
          gap: isMobile ? '10px' : '14px',
          marginBottom: '16px'
        }}>
          {/* Total Products */}
          <div onClick={() => setFilter('all')} style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: '20px', padding: isMobile ? '16px 14px' : '20px',
            position: 'relative', overflow: 'hidden', cursor: 'pointer',
            border: filter === 'all' ? '2px solid #3478f6' : '1px solid #e2e8f0',
            transition: 'all 0.2s'
          }}>
            <div style={{ position: 'absolute', top: '-15px', right: '-15px', opacity: 0.07 }}>
              <svg width={isMobile ? 60 : 80} height={isMobile ? 60 : 80} viewBox="0 0 24 24" fill="none" stroke="#1e293b" strokeWidth="1.5"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <svg width={isMobile ? 18 : 22} height={isMobile ? 18 : 22} viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: isMobile ? '11px' : '18px', fontWeight: '700', color: '#16a34a' }}>{totalUnits} {language === 'ar' ? 'متبقي' : 'left'}</span>
                {totalSold > 0 && <span style={{ fontSize: isMobile ? '11px' : '18px', fontWeight: '700', color: '#94a3b8', marginLeft: '4px' }}>· {totalSold} {language === 'ar' ? 'مباع' : 'sold'}</span>}
              </div>
            </div>
            <div style={{ fontSize: isMobile ? '36px' : '48px', fontWeight: '900', lineHeight: 1, letterSpacing: '-2px', color: '#1e293b' }}>
              {summary.totalProducts}
            </div>
            <div style={{ fontSize: isMobile ? '11px' : '14px', fontWeight: '700', color: '#94a3b8', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {language === 'ar' ? 'المنتجات' : 'Products'}
            </div>
          </div>

          {/* In Stock */}
          <div style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: '20px', padding: isMobile ? '16px 14px' : '20px',
            position: 'relative', overflow: 'hidden',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ position: 'absolute', top: '-15px', right: '-15px', opacity: 0.07 }}>
              <svg width={isMobile ? 60 : 80} height={isMobile ? 60 : 80} viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="1.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <svg width={isMobile ? 18 : 22} height={isMobile ? 18 : 22} viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              {summary.totalProducts > 0 && <span style={{ fontSize: isMobile ? '11px' : '18px', fontWeight: '700', color: '#059669' }}>{Math.round((summary.inStock / summary.totalProducts) * 100)}%</span>}
            </div>
            <div style={{ fontSize: isMobile ? '36px' : '48px', fontWeight: '900', lineHeight: 1, letterSpacing: '-2px', color: '#059669' }}>
              {summary.inStock}
            </div>
            <div style={{ fontSize: isMobile ? '11px' : '14px', fontWeight: '700', color: '#94a3b8', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {language === 'ar' ? 'متوفر' : 'In Stock'}
            </div>
          </div>

          {/* Low Stock */}
          <div onClick={() => setFilter('low')} style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: '20px', padding: isMobile ? '16px 14px' : '20px',
            position: 'relative', overflow: 'hidden', cursor: 'pointer',
            border: filter === 'low' ? '2px solid #d97706' : '1px solid #e2e8f0',
            transition: 'all 0.2s'
          }}>
            <div style={{ position: 'absolute', top: '-15px', right: '-15px', opacity: 0.07 }}>
              <svg width={isMobile ? 60 : 80} height={isMobile ? 60 : 80} viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="1.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <svg width={isMobile ? 18 : 22} height={isMobile ? 18 : 22} viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span style={{ fontSize: isMobile ? '11px' : '18px', fontWeight: '700', color: '#d97706' }}>&le;5</span>
            </div>
            <div style={{ fontSize: isMobile ? '36px' : '48px', fontWeight: '900', lineHeight: 1, letterSpacing: '-2px', color: '#d97706' }}>
              {summary.lowStock}
            </div>
            <div style={{ fontSize: isMobile ? '11px' : '14px', fontWeight: '700', color: '#94a3b8', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {language === 'ar' ? 'منخفض' : 'Low Stock'}
            </div>
          </div>

          {/* Out of Stock */}
          <div onClick={() => setFilter('out')} style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: '20px', padding: isMobile ? '16px 14px' : '20px',
            position: 'relative', overflow: 'hidden', cursor: 'pointer',
            border: filter === 'out' ? '2px solid #dc2626' : '1px solid #e2e8f0',
            transition: 'all 0.2s'
          }}>
            <div style={{ position: 'absolute', top: '-15px', right: '-15px', opacity: 0.07 }}>
              <svg width={isMobile ? 60 : 80} height={isMobile ? 60 : 80} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <svg width={isMobile ? 18 : 22} height={isMobile ? 18 : 22} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              <span style={{ fontSize: isMobile ? '11px' : '18px', fontWeight: '700', color: '#dc2626' }}>0</span>
            </div>
            <div style={{ fontSize: isMobile ? '36px' : '48px', fontWeight: '900', lineHeight: 1, letterSpacing: '-2px', color: '#dc2626' }}>
              {summary.outOfStock}
            </div>
            <div style={{ fontSize: isMobile ? '11px' : '14px', fontWeight: '700', color: '#94a3b8', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {language === 'ar' ? 'نفذ' : 'Out of Stock'}
            </div>
          </div>
        </div>

        {/* ====== Stock Value Bar + Actions ====== */}
        <div style={{
          background: 'linear-gradient(135deg, #3478f6, #2563eb)', borderRadius: '16px',
          padding: isMobile ? '14px 16px' : '16px 20px', marginBottom: '16px',
          color: '#fff', display: 'flex', alignItems: 'center', gap: '12px'
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? '11px' : '14px', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '700' }}>
              {language === 'ar' ? 'قيمة المخزون' : 'Stock Value'}
            </div>
            <div style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: '900', marginTop: '2px', letterSpacing: '-1px' }}>
              AED {summary.totalStockValue.toLocaleString()}
            </div>
          </div>
          <button onClick={() => { setShowLogs(!showLogs); setLogProductId(null); }} style={{
            background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff',
            padding: isMobile ? '10px 16px' : '12px 20px', borderRadius: '12px', cursor: 'pointer',
            fontSize: isMobile ? '12px' : '14px', fontWeight: '700', whiteSpace: 'nowrap'
          }}>
            {showLogs ? (language === 'ar' ? 'إخفاء' : 'Hide') : (language === 'ar' ? 'السجل' : 'Log')}
          </button>
          <button onClick={recalculateStock} disabled={recalculating} style={{
            background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff',
            padding: isMobile ? '10px 16px' : '12px 20px', borderRadius: '12px', cursor: recalculating ? 'wait' : 'pointer',
            fontSize: isMobile ? '12px' : '14px', fontWeight: '700', whiteSpace: 'nowrap', opacity: recalculating ? 0.6 : 1
          }}>
            {recalculating ? '...' : (language === 'ar' ? 'حساب' : 'Recalc')}
          </button>
        </div>

        {/* ====== Stock Log ====== */}
        {showLogs && (
          <div style={{
            background: 'white', borderRadius: '20px', border: '1px solid #e5e7eb',
            padding: isMobile ? '16px' : '20px', marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1e293b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4l3 3"/><circle cx="12" cy="12" r="10"/></svg>
                <span style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: '#1e293b' }}>
                  {language === 'ar' ? 'سجل المخزون' : 'Stock Log'}
                </span>
                {logProductId && <span style={{ fontSize: isMobile ? '11px' : '14px', fontWeight: '600', color: '#64748b', background: '#f1f5f9', padding: '2px 8px', borderRadius: '6px' }}>
                  {products.find(p => p.id === logProductId)?.name || logProductId}
                </span>}
              </div>
              {logProductId && (
                <button onClick={() => setLogProductId(null)} style={{ background: '#f1f5f9', border: 'none', padding: '6px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#64748b', fontWeight: '600' }}>
                  {language === 'ar' ? 'الكل' : 'All'}
                </button>
              )}
            </div>
            {displayLogs.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#9ca3af', padding: '24px 0', fontSize: '14px' }}>{language === 'ar' ? 'لا توجد سجلات' : 'No activity yet'}</p>
            ) : (
              <div style={{ maxHeight: '350px', overflow: 'auto' }}>
                {displayLogs.map((log, i) => (
                  <div key={log.id || i} style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '14px', padding: isMobile ? '10px 0' : '12px 0', borderBottom: i < displayLogs.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <div style={{
                      width: isMobile ? '36px' : '42px', height: isMobile ? '36px' : '42px', borderRadius: '10px',
                      background: log.change > 0 ? '#f0fdf4' : '#fef2f2',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: isMobile ? '14px' : '16px', fontWeight: '800',
                      color: log.change > 0 ? '#16a34a' : '#dc2626', flexShrink: 0
                    }}>
                      {log.change > 0 ? '+' : ''}{log.change}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: isMobile ? '13px' : '15px', fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.productName || log.productId}
                      </div>
                      <div style={{ fontSize: isMobile ? '11px' : '14px', color: '#9ca3af', marginTop: '1px' }}>
                        {log.previousStock} → {log.newStock}
                        {log.orderId && <span> · #{log.orderId}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: isMobile ? '11px' : '14px', fontWeight: '700', color: getReasonColor(log.reason), background: getReasonColor(log.reason) + '15', padding: '2px 8px', borderRadius: '6px', display: 'inline-block' }}>
                        {getReasonLabel(log.reason)}
                      </div>
                      <div style={{ fontSize: isMobile ? '10px' : '14px', color: '#9ca3af', marginTop: '3px' }}>{formatDate(log.createdAt)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== Search ====== */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ position: 'relative' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={language === 'ar' ? 'بحث عن منتج...' : 'Search products...'}
              style={{
                width: '100%', padding: '12px 16px 12px 42px',
                border: '1.5px solid #e2e8f0', borderRadius: '14px',
                fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                direction: isRTL ? 'rtl' : 'ltr', background: 'white'
              }}
            />
          </div>
        </div>

        {/* ====== Products Stock Cards ====== */}
          {filteredProducts.length === 0 ? (
            <div style={{ background: 'white', borderRadius: '20px', border: '1px solid #e5e7eb', padding: '48px 16px', textAlign: 'center' }}>
              <p style={{ color: '#9ca3af', fontSize: '15px' }}>{language === 'ar' ? 'لا توجد منتجات' : 'No products found'}</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: isMobile ? '12px' : '16px'
            }}>
              {filteredProducts.map((product) => {
                const badge = getStockBadge(product.stock);
                const isAdjusting = adjusting === product.id;
                const originalStock = product.baseStock || 0;
                const sold = Math.max(0, originalStock - product.stock);
                const soldPct = originalStock > 0 ? Math.round((sold / originalStock) * 100) : 0;
                const stockLeft = product.stock;
                return (
                  <div key={product.id} style={{
                    background: isAdjusting ? '#eff6ff' : 'white',
                    borderRadius: '20px',
                    border: isAdjusting ? '1.5px solid #bfdbfe' : '1px solid #e5e7eb',
                    padding: isMobile ? '16px' : '22px',
                    transition: 'all 0.15s',
                    opacity: !product.enabled ? 0.5 : 1,
                    display: 'flex', flexDirection: 'column'
                  }}>
                    {/* Header: Image + Name + Badge */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: isMobile ? '14px' : '18px' }}>
                      <div style={{ width: isMobile ? '48px' : '56px', height: isMobile ? '48px' : '56px', borderRadius: '14px', overflow: 'hidden', flexShrink: 0, background: '#f1f5f9' }}>
                        {product.image && <img src={product.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: isMobile ? '15px' : '18px', fontWeight: '700', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {language === 'ar' && product.nameAr ? product.nameAr : product.name}
                        </div>
                        <div style={{ fontSize: isMobile ? '14px' : '15px', color: '#9ca3af', marginTop: '2px' }}>
                          AED {product.price}
                          {!product.enabled && <span style={{ color: '#ef4444', marginLeft: '8px', fontSize: isMobile ? '12px' : '14px', fontWeight: '700' }}>{language === 'ar' ? 'مخفي' : 'Hidden'}</span>}
                        </div>
                      </div>
                      <div style={{
                        padding: isMobile ? '4px 12px' : '6px 16px', borderRadius: '50px', fontSize: isMobile ? '12px' : '14px', fontWeight: '800',
                        color: badge.color, background: badge.bg, border: `1px solid ${badge.border}`, flexShrink: 0
                      }}>{badge.text}</div>
                    </div>

                    {/* Big Numbers Row: Available + Sold + Base */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: originalStock > 0 ? '1fr 1fr 1fr' : '1fr 1fr',
                      gap: isMobile ? '8px' : '10px',
                      marginBottom: isMobile ? '14px' : '18px'
                    }}>
                      {/* Available */}
                      <div style={{
                        background: stockLeft <= 5 ? '#fef2f2' : '#ecfdf5',
                        borderRadius: '14px',
                        padding: isMobile ? '14px 10px' : '18px 12px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          fontSize: isMobile ? '32px' : '42px', fontWeight: '900',
                          color: stockLeft <= 5 ? '#dc2626' : '#059669',
                          lineHeight: 1, letterSpacing: '-2px'
                        }}>{stockLeft}</div>
                        <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '700', color: stockLeft <= 5 ? '#dc2626' : '#059669', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '6px' }}>
                          {language === 'ar' ? 'متوفر' : 'Available'}
                        </div>
                      </div>
                      {/* Sold */}
                      <div style={{
                        background: '#eff6ff',
                        borderRadius: '14px',
                        padding: isMobile ? '14px 10px' : '18px 12px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          fontSize: isMobile ? '32px' : '42px', fontWeight: '900',
                          color: '#3478f6',
                          lineHeight: 1, letterSpacing: '-2px'
                        }}>{sold}</div>
                        <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '700', color: '#3478f6', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '6px' }}>
                          {language === 'ar' ? 'مباع' : 'Sold'}
                        </div>
                      </div>
                      {/* Base */}
                      {originalStock > 0 && (
                        <div style={{
                          background: '#f8fafc',
                          borderRadius: '14px',
                          padding: isMobile ? '14px 10px' : '18px 12px',
                          textAlign: 'center'
                        }}>
                          <div style={{
                            fontSize: isMobile ? '32px' : '42px', fontWeight: '900',
                            color: '#64748b',
                            lineHeight: 1, letterSpacing: '-2px'
                          }}>{originalStock}</div>
                          <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '6px' }}>
                            {language === 'ar' ? 'الأساسي' : 'Base'}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Progress bar */}
                    {originalStock > 0 && (
                      <div style={{ marginBottom: isMobile ? '14px' : '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <span style={{ fontSize: isMobile ? '14px' : '21px', fontWeight: '800', color: '#64748b' }}>
                            {sold} {language === 'ar' ? 'مباع' : 'sold'}
                          </span>
                          <span style={{
                            fontSize: isMobile ? '18px' : '24px', fontWeight: '900', letterSpacing: '-0.5px',
                            color: soldPct > 80 ? '#dc2626' : soldPct > 50 ? '#d97706' : '#059669'
                          }}>{soldPct}%</span>
                        </div>
                        <div style={{ height: isMobile ? '8px' : '10px', background: '#e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', width: `${Math.min(100, soldPct)}%`,
                            background: soldPct > 80 ? 'linear-gradient(90deg, #ef4444, #dc2626)' : soldPct > 50 ? 'linear-gradient(90deg, #f59e0b, #d97706)' : 'linear-gradient(90deg, #34d399, #059669)',
                            borderRadius: '10px', transition: 'width 0.4s ease'
                          }} />
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: 'auto' }}>
                      {isAdjusting ? (
                        <>
                          <input type="number" value={adjustValue} onChange={e => setAdjustValue(e.target.value)} autoFocus placeholder={language === 'ar' ? 'الكمية' : 'qty'}
                            style={{ flex: 1, padding: '10px', border: '1.5px solid #bfdbfe', borderRadius: '12px', fontSize: '16px', textAlign: 'center', background: 'white', fontWeight: '700' }}
                            onKeyDown={e => { if (e.key === 'Enter' && adjustValue) setStock(product.id, parseInt(adjustValue)); if (e.key === 'Escape') { setAdjusting(null); setAdjustValue(''); } }}
                          />
                          <button onClick={() => { if (adjustValue) setStock(product.id, parseInt(adjustValue)); }} style={{ background: '#3478f6', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '12px', cursor: 'pointer', fontSize: '14px', fontWeight: '700' }}>{language === 'ar' ? 'حفظ' : 'Set'}</button>
                          <button onClick={() => { setAdjusting(null); setAdjustValue(''); }} style={{ background: '#f1f5f9', border: 'none', padding: '10px 14px', borderRadius: '12px', cursor: 'pointer', fontSize: '14px', color: '#64748b', fontWeight: '700' }}>✕</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => quickAdjust(product.id, -1)} style={{ background: '#fef2f2', border: '1px solid #fecaca', height: '40px', width: '40px', borderRadius: '12px', cursor: 'pointer', fontSize: '20px', fontWeight: '800', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>−</button>
                          <button onClick={() => quickAdjust(product.id, 1)} style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', height: '40px', width: '40px', borderRadius: '12px', cursor: 'pointer', fontSize: '20px', fontWeight: '800', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>+</button>
                          <div style={{ flex: 1 }} />
                          <button onClick={() => { setAdjusting(product.id); setAdjustValue(String(product.stock)); }} style={{ background: '#f1f5f9', border: 'none', height: '40px', padding: '0 16px', borderRadius: '12px', cursor: 'pointer', fontSize: '14px', color: '#64748b', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                            {language === 'ar' ? 'تعديل' : 'Edit'}
                          </button>
                          <button onClick={() => { setLogProductId(product.id); setShowLogs(true); }} style={{ background: '#f1f5f9', border: 'none', height: '40px', padding: '0 16px', borderRadius: '12px', cursor: 'pointer', fontSize: '14px', color: '#64748b', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4l3 3"/><circle cx="12" cy="12" r="10"/></svg>
                            {language === 'ar' ? 'سجل' : 'Log'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
};

export default Inventory;
